// User.tsx

import { Document } from '@/DTOS/User/Document';
import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateProps } from '../../contextRedux/User/UserModel';
import { setUsers } from '../../contextRedux/User/UserStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import { User as UserResponse } from '../../DTOS/User/Response/User';
import UserService from '../../services/user/UserService';
import { RootState } from '../../store';
import { saveBlobCrossPlatform } from '../../utils/saveBlobCrossPlatform';
import UserFileModal from './UserFileModal/UserFileModal';
import UserTable from './UserTable/UserTable';

const User: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.user?.props?.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.user?.props?.skip);
  const take = useSelector((state: RootState) => state.models?.user?.props?.take);
  const refresh = useSelector((state: RootState) => state.models?.user?.props?.refresh);

  const [id, setId] = useState<string>('');
  const [modalFileVisible, setModalFileVisible] = useState<boolean>(false);
  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);
  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });
  const handleFileCancel = () => setModalFileVisible(false);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;

    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsParamsInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isParamsInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;

      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };

        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });

        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isParamsInitialized, navigate, location.pathname, location.search]);

  const {
    data: _usersData,
    isFetching: isUsersLoading,
    isError: isErrorOnFetchUsers,
    error: usersError,
    refetch,
  } = useQuery({
    queryKey: ['users', searchTerm, skip, take, refresh],
    queryFn: () =>
      UserService.search({
        termen: searchTerm || undefined,
        skip,
        take,
        isGroupIncluded: true,
        isPersonalDataIncluded: true,
        isUserAdministrationCompaniesIncluded: true,
      }).then((data: any) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  const handleDoc = useCallback((record: UserResponse) => {
    setId(record?.id);
    setModalFileVisible(true);
  }, []);

  const onFinishDocs = (doc: Document) => {
    UserService.docs(doc)
      .then((response) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'file.pdf';

        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        return response.blob().then((blob: any) => {
          saveBlobCrossPlatform(blob, filename);
        });
      })
      .catch((error) => {
        console.error('Error processing your request:', error);
      });
  };

  if (isUsersLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchUsers) {
    return <Alert message={(usersError as Error).message} type='error' />;
  }

  return (
    <>
      <h1 style={{ marginBottom: 50 }}>Utilizator</h1>
      <AnimateButton onClick={() => window.open(`/administration/user/new`, '_blank')}>Adauga</AnimateButton>
      <UserFileModal
        id={id!}
        modalFileVisible={modalFileVisible}
        handleFileCancel={handleFileCancel}
        handleDocs={onFinishDocs}
      />
      <UserTable handleDocs={handleDoc} />
    </>
  );
};

export default User;
